import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap, filter, catchError, map, tap, first } from 'rxjs/operators';

import { ABTestingService } from '../services/AB-testing.service';

import {
    Init,
    InitOptimizely,
    InitOptimizelySkipped,
    InitOptimizelyError,
    InitOptimizelySuccess,
    SetListenersSuccessAction,
    SetListenersErrorAction,
    OptimizelySkippingReasons,
} from './AB-testing.actions';
import { getIsOptimizelyLoaded, getIsListenersLoaded } from './AB-testing.selectors';

// TODO ECOMDUTI-21012 [TechDebt][Data] - Dependencias de Acciones pertenecientes a módulos lazy. Refactorizar.
import { UserConsentService } from '@core/ui/services/user-consent.service';
import { ItxObservabilityCommands } from '@frontend/libs/itx-observability-core';
import { LoadProductPage } from '@pages/product/store/product-page.actions';

@Injectable()
export class ABTestingEffects {
    private loaded = false;

    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly abTestingService: ABTestingService,
        private readonly userConsentService: UserConsentService,
    ) { }

    /**
     * Check if Optimizely has been initialized. If it's not, demands the initialization and does nothing otherwise.
     */
    public checkOptimizelyInitialization$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(Init),
            concatLatestFrom(() => this.store.select(getIsOptimizelyLoaded)),
            map(([_, itHasBeenInitialized]) => {
                if (!itHasBeenInitialized) {
                    return InitOptimizely();
                }
                return InitOptimizelySkipped({ reason: OptimizelySkippingReasons.ALREADY_BEEN_LOADED });
            }),
        );
    });

    public initOptimizely$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitOptimizely),
            switchMap(() => this.userConsentService.waitForAnalyticsAndFunctionalPCookieConsent().pipe(first())),
            filter((hasTheUserConsented) => !!hasTheUserConsented),
            switchMap(() => this.abTestingService.initOptimizely()),
            map((success) => {
                if (!success) {
                    const error = new Error('Unable to initialize Optimizely for unknown reasons');
                    return InitOptimizelyError({ error });
                }
                return InitOptimizelySuccess();
            }),
            // eslint-disable-next-line rxjs/no-unsafe-catch
            catchError((error: string) => {
                return of(InitOptimizelyError({ error }));
            }),
        );
    });

    public setListeners$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitOptimizelySuccess),
            concatLatestFrom(() => [
                this.store.select(getIsListenersLoaded),
                this.store.select(getIsOptimizelyLoaded),
            ]),
            filter(([_action, isListenersLoaded, isOptimizelyLoaded]) =>
                // NOTE: Los listeners se setean una única vez cuando alguna de las herramientas de test ab se haya inicializado
                isOptimizelyLoaded && !isListenersLoaded && !this.loaded),
            map(() => {
                try {
                    this.abTestingService.setListeners();
                } catch (error: unknown) {
                    console.warn(error);
                    ItxObservabilityCommands.sendObservabilityLog(error as string, 'ABTestingEffects setListeners');
                    return SetListenersErrorAction();
                }
                this.loaded = true;
                return SetListenersSuccessAction();
            }),
        );
    });

    public initTestAbSystem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetListenersSuccessAction),
            tap(() => {
                this.abTestingService.launchOptimizelyActivateEvent('init');
                this.abTestingService.launchOptimizelyActivateEvent('init_localhost');
            }),
        );
    }, { dispatch: false });

    public initProductPageTestAb$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadProductPage),
            tap(() => {
                this.abTestingService.launchOptimizelyActivateEvent('product_page');
            }),
        );
    }, { dispatch: false });
}
